import React from "react";

import { Card, Typography, Stack, Box } from "@mui/material";
import { Link, useParams, useLocation } from "react-router-dom";

import SupportedLanguages from "./SupportedLanguages";

const FormManagementOverview: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const location = useLocation();

  const app = location.pathname.includes("rubiintel")
    ? "rubiintel"
    : "rubireportv2";
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Form Management
      </Typography>

      <Typography variant="body1" paragraph>
        Form management consists of two main components: Configurations and
        Surveys. These work together to create the complete form experience for
        reporting parties.
      </Typography>

      <Stack spacing={3}>
        {/* Configurations Section */}
        <Card sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Configurations
          </Typography>
          <Typography variant="body1" paragraph>
            Configurations control what reporting parties see when accessing
            forms. This includes the form's title, description, availability
            settings, and overall presentation. Each configuration references a
            survey that contains the actual questions.
          </Typography>
          <Link to={`/${app}/${orgId}/forms/configs`}>
            View Form Configurations
          </Link>
        </Card>

        {/* Surveys Section */}
        <Card sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Surveys
          </Typography>
          <Typography variant="body1" paragraph>
            Surveys contain the actual questions and content that make up a
            form. They are referenced by configurations and can be reused across
            multiple configurations. This separation allows for efficient form
            management and version control.
          </Typography>
          <Link to={`/${app}/${orgId}/forms/surveys`}>View Surveys</Link>
        </Card>

        {/* Dropdown Configuration Section */}
        <Card sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Dropdown Configurations
          </Typography>
          <Typography variant="body1" paragraph>
            Manage dropdown option lists for form questions. This includes
            organization-specific lists and shared lists available across the
            platform. These lists can be referenced in survey questions to
            provide consistent dropdown options.
          </Typography>
          <Link to={`/${app}/${orgId}/forms/dropdowns`}>
            View Dropdown Configurations
          </Link>
        </Card>

        {/* Survey Groups Section */}
        <Card sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Survey Groups
          </Typography>
          <Typography variant="body1" paragraph>
            Organize survey configurations into categories displayed on the
            public report selection page. Groups control what citizens see and
            in what order. Surveys can belong to multiple groups.
          </Typography>
          <Link to={`/${app}/${orgId}/forms/groups`}>Manage Survey Groups</Link>
        </Card>

        {/* Crime Codes Section */}
        <Card sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Crime Codes
          </Typography>
          <Typography variant="body1" paragraph>
            Manage crime codes (UCR/Statute codes) for your organization. Define
            codes that can be automatically or manually assigned to reports
            based on rules. Configure which report types each code is visible
            on.
          </Typography>
          <Link to={`/${app}/${orgId}/forms/crime-codes`}>
            View Crime Codes
          </Link>
        </Card>

        <SupportedLanguages />
      </Stack>
    </Box>
  );
};

export default FormManagementOverview;
